"use client";

import { Phone, Clock, MessageCircle, TrendingUp, Loader2, Mail } from "lucide-react";
import React from "react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";
import { Progress } from "@/app/_module/components/ui/progress";
import { useCommunicationStats, communicationQueryKeys } from "@/app/_module/hooks/useCommunicationService";
import AgencyTokenManager from "@/app/_module/services/agencyTokenManager";
import { useBranding } from "@/app/_module/store/contexts/brandingContext";
import { useQueryClient } from "@tanstack/react-query";

interface CommunicationMetricsProps {
  totalCalls?: number;
  totalTime?: number;
  avgCallDuration?: number;
  totalSMS?: number;
  totalEmails?: number;
  faxCount?: number;
  isLoading?: boolean;
  onRefresh?: () => void;
  /**
   * Dashboard date filter. Load-bearing: this component fetches its OWN stats below and
   * prefers them over the props, so passing the range to the parent alone did nothing —
   * the box kept reporting all time while everything around it followed the filter.
   */
  startDate?: string;
  endDate?: string;
}

export const CommunicationMetrics = function({
  totalCalls = 0,
  totalTime = 0,
  avgCallDuration = 0,
  totalSMS = 0,
  totalEmails = 0,
  faxCount = 0,
  isLoading = false,
  onRefresh,
  startDate,
  endDate
}: CommunicationMetricsProps) {
  const { isWhiteLabeled } = useBranding();
  const queryClient = useQueryClient();
  const [currentAgencyId, setCurrentAgencyId] = React.useState<string | null>(
    AgencyTokenManager.getCurrentAgencyId()
  );

  // Invalidate and refetch stats when agency changes via event
  React.useEffect(() => {
    const handleAgencyChange = () => {
      const newAgencyId = AgencyTokenManager.getCurrentAgencyId();
      setCurrentAgencyId(newAgencyId);
      queryClient.invalidateQueries({ queryKey: ['communication', 'stats'] });
    };

    window.addEventListener('insuracentral-agency-change', handleAgencyChange);

    return () => {
      window.removeEventListener('insuracentral-agency-change', handleAgencyChange);
    };
  }, [queryClient]);

  // Fetch real-time stats with auto-refresh
  const { data: liveStats, refetch, isLoading: isStatsLoading, error } = useCommunicationStats({
    startDate,
    endDate,
  });

  // Use live stats if available, otherwise use props
  const stats = liveStats || {
    totalCalls,
    totalTime,
    avgCallDuration,
    totalSMS,
    emailCount: totalEmails,
    faxCount,
    answeredCalls: 0,
    callAnswerRate: 0,
    weeklyTotalCalls: 0,
    weeklyAnsweredCalls: 0,
    weeklyAnswerRate: 0,
  };

  // Format time duration
  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const metrics = {
    totalCalls: stats.totalCalls,
    answeredCalls: stats.answeredCalls,
    weeklyTotalCalls: stats.weeklyTotalCalls || 0,
    weeklyAnsweredCalls: stats.weeklyAnsweredCalls || 0,
    weeklyAnswerRate: stats.weeklyAnswerRate || 0,
    totalTalkTime: formatDuration(stats.totalTime),
    avgCallDuration: formatDuration(stats.avgCallDuration),
    smssent: stats.totalSMS,
    emailsSent: stats.emailCount,
    faxCount: stats.faxCount,
    callAnswerRate: stats.callAnswerRate,
    callGoal: 200,
  };

  // Use weekly calls for goal progress (resets each week)
  const callProgress = metrics.callGoal > 0 ? (metrics.weeklyTotalCalls / metrics.callGoal) * 100 : 0;

  if (isLoading) {
    return (
      <Card className="h-full shadow-sm border-gray-200">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-lg font-semibold text-gray-900">
            Communication
          </CardTitle>
          <Phone className="h-5 w-5 text-primary" />
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-center p-8">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
              <p className="text-sm text-gray-600">Loading communication data...</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full shadow-sm border-gray-200">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-lg font-semibold text-gray-900">
          Communication
        </CardTitle>
        <Phone className="h-5 w-5 text-primary" />
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Call Statistics with improved visual design */}
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className={`text-center p-4 bg-gradient-to-br ${isWhiteLabeled ? 'from-primary/10 to-primary/20 border-primary/20' : 'from-blue-50 to-blue-100 border-blue-200'} rounded-lg border min-h-[80px] flex flex-col justify-center`}>
              <div className={`text-2xl font-bold ${isWhiteLabeled ? 'text-primary' : 'text-blue-600'}`}>
                {metrics.totalCalls}
              </div>
              <div className={`text-sm font-medium ${isWhiteLabeled ? 'text-primary' : 'text-blue-600'}`}>
                Total Calls
              </div>
            </div>
            <div className={`text-center p-4 bg-gradient-to-br ${isWhiteLabeled ? 'from-secondary/10 to-secondary/20 border-secondary/20' : 'from-green-50 to-green-100 border-green-200'} rounded-lg border min-h-[80px] flex flex-col justify-center`}>
              <div className={`text-2xl font-bold ${isWhiteLabeled ? 'text-secondary' : 'text-green-600'}`}>
                {metrics.answeredCalls}
              </div>
              <div className={`text-sm font-medium ${isWhiteLabeled ? 'text-secondary' : 'text-green-600'}`}>
                Answered
              </div>
            </div>
          </div>

          {/* Call Goal Progress with better styling - uses weekly stats that reset each Monday */}
          <div className="space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex justify-between items-center">
              <span className="font-semibold text-gray-900 text-sm">
                Weekly Call Goal
              </span>
              <span className="text-sm text-gray-600 font-medium">
                {metrics.weeklyTotalCalls}/{metrics.callGoal}
              </span>
            </div>
            <Progress className="h-3" value={Math.min(callProgress, 100)} />
            <div className="flex justify-between text-xs">
              <span className="text-gray-600 font-medium">{Math.min(callProgress, 100).toFixed(0)}% complete</span>
              <span className="text-gray-500">{metrics.weeklyAnswerRate.toFixed(1)}% answer rate</span>
            </div>
          </div>
        </div>

        {/* Time Metrics with improved layout */}
        <div className="space-y-3">
          <h4 className="font-semibold text-gray-900 text-sm">
            Time & Duration
          </h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Clock className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">
                  Total talk time
                </span>
              </div>
              <span className="font-semibold text-gray-900 text-sm">
                {metrics.totalTalkTime}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <TrendingUp className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">
                  Avg. Call Duration
                </span>
              </div>
              <span className="font-semibold text-gray-900 text-sm">
                {metrics.avgCallDuration}
              </span>
            </div>
          </div>
        </div>

        {/* Other Communication with better grid layout */}
        <div className="space-y-3">
          <h4 className="font-semibold text-gray-900 text-sm">
            Other Communications
          </h4>
          <div className="grid grid-cols-1 gap-3">
            <div className="flex items-center gap-3 p-3 bg-primary/10 rounded-lg border border-primary/20">
              <MessageCircle className="h-5 w-5 text-primary" />
              <div className="flex-1">
                <div className="font-semibold text-primary">
                  {metrics.smssent}
                </div>
                <div className="text-xs text-primary font-medium">
                  SMS Sent
                </div>
              </div>
            </div>
            <div className={`flex items-center gap-3 p-3 ${isWhiteLabeled ? 'bg-secondary/10 border-secondary/20' : 'bg-teal-50 border-teal-200'} rounded-lg border`}>
              <Mail className={`h-5 w-5 ${isWhiteLabeled ? 'text-secondary' : 'text-teal-600'}`} />
              <div className="flex-1">
                <div className={`font-semibold ${isWhiteLabeled ? 'text-secondary' : 'text-teal-600'}`}>
                  {metrics.emailsSent}
                </div>
                <div className={`text-xs font-medium ${isWhiteLabeled ? 'text-secondary' : 'text-teal-600'}`}>
                  Emails Sent
                </div>
              </div>
            </div>
            <div className={`flex items-center gap-3 p-3 ${isWhiteLabeled ? 'bg-accent/10 border-accent/20' : 'bg-purple-50 border-purple-200'} rounded-lg border`}>
              <svg className={`h-5 w-5 ${isWhiteLabeled ? 'text-accent' : 'text-purple-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <div className="flex-1">
                <div className={`font-semibold ${isWhiteLabeled ? 'text-accent' : 'text-purple-600'}`}>
                  {faxCount}
                </div>
                <div className={`text-xs font-medium ${isWhiteLabeled ? 'text-accent' : 'text-purple-600'}`}>
                  Faxes Sent
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
